import {
  apaDate,
  mlaDate,
  type PartialDate,
} from "@/lib/tool-kit/citation-parts";
import { titleCase as sharedTitleCase } from "@/lib/tool-kit/legal-sources/common";
import {
  chicagoDateRange,
  chicagoShortTitle,
  longDate,
  mlaDateRange,
} from "@/lib/tool-kit/media-sources/common";

/*
 * Pieces the citation-sources-more generators share.
 */

const RE_WORDS = /\s+/;

/** Prepositions and conjunctions that should never end a shortened title. */
const DANGLING = new Set([
  "a",
  "after",
  "against",
  "among",
  "an",
  "and",
  "as",
  "at",
  "before",
  "between",
  "by",
  "during",
  "for",
  "from",
  "in",
  "into",
  "of",
  "on",
  "or",
  "over",
  "the",
  "through",
  "to",
  "toward",
  "towards",
  "under",
  "versus",
  "vs",
  "with",
  "without",
  "can",
  "could",
  "do",
  "does",
  "how",
  "is",
  "may",
  "might",
  "should",
  "was",
  "what",
  "when",
  "where",
  "which",
  "who",
  "why",
  "will",
  "would",
]);

/**
 * Chicago's shortened-note title: up to four key words from the main title, never
 * ending on a small word ("Gut Microbiome Dynamics under" → "Gut Microbiome
 * Dynamics"). Chicago picks key words by hand, so this is a starting point to check.
 */
export function shortNoteTitle(title: string): string {
  const words = chicagoShortTitle(title).split(RE_WORDS).filter(Boolean);
  while (words.length > 2 && DANGLING.has((words.at(-1) ?? "").toLowerCase())) {
    words.pop();
  }
  return words.join(" ");
}

/* ------------------------------------------------------------ running time */

const RE_CLOCK = /^(?:(\d+):)?(\d{1,2}):(\d{2})$/;

/**
 * Chicago's running time for a talk or video: "9:15" → "9 min., 15 sec.",
 * "1:01:45" → "1 hr., 1 min., 45 sec." (CMOS 18 Quick Guide: "9 min., 15 sec."). Text
 * that is not a clock time, such as "9 min., 15 sec.", is returned as typed.
 */
export function talkLength(text: string): string {
  const trimmed = text.trim();
  const match = RE_CLOCK.exec(trimmed);
  if (!match) {
    return trimmed;
  }
  const pieces: string[] = [];
  const hours = Number(match[1] ?? 0);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  if (hours > 0) {
    pieces.push(`${hours} hr.`);
  }
  if (minutes > 0) {
    pieces.push(`${minutes} min.`);
  }
  if (seconds > 0) {
    pieces.push(`${seconds} sec.`);
  }
  return pieces.length > 0 ? pieces.join(", ") : trimmed;
}

const RE_ENDS_IN_MARK = /[?!]$/;

/**
 * A title in curly quotation marks with the punctuation that follows it placed the
 * American way: “Title,” — or “Title?” with no comma when the title ends in its own
 * mark. Pass "" when nothing follows.
 */
export function quotedTitle(title: string, mark: "," | "." | ""): string {
  if (mark === "" || RE_ENDS_IN_MARK.test(title)) {
    return `“${title}”`;
  }
  return `“${title}${mark}”`;
}

/* ---------------------------------------------------------- meeting dates */

const RE_YEAR_TAIL = / \d{4}$/;

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** True when both dates give a day in the same month of the same year. */
function sameMonth(start: PartialDate, end: PartialDate): boolean {
  return (
    start.year === end.year &&
    start.month !== undefined &&
    start.month === end.month &&
    start.day !== undefined &&
    end.day !== undefined &&
    end.day !== start.day
  );
}

/**
 * APA's date for a meeting: the whole run of the conference, "2016, April 26–28"; across
 * months "2019, May 30–June 2". No date → "n.d.".
 */
export function apaMeetingDates(
  start: PartialDate | null,
  end: PartialDate | null
): string {
  if (!start) {
    return "n.d.";
  }
  if (!end || (start.year === end.year && start.month === undefined)) {
    return apaDate(start);
  }
  if (sameMonth(start, end)) {
    return `${start.year}, ${MONTH_NAMES[(start.month ?? 1) - 1]} ${start.day}–${end.day}`;
  }
  if (
    start.year === end.year &&
    start.month &&
    end.month &&
    start.day &&
    end.day
  ) {
    const from = `${MONTH_NAMES[start.month - 1]} ${start.day}`;
    const to = `${MONTH_NAMES[end.month - 1]} ${end.day}`;
    return `${start.year}, ${from}–${to}`;
  }
  return `${apaDate(start)}–${apaDate(end)}`;
}

/** MLA's abbreviated month: "Apr.", "May", "June", "Sept.". */
function mlaMonth(month: number): string {
  return mlaDate({ year: 2000, month }).replace(RE_YEAR_TAIL, "");
}

/** MLA's meeting date: "26-28 Apr. 2016"; across months "30 May-2 June 2019". */
export function mlaMeetingDates(
  start: PartialDate | null,
  end: PartialDate | null
): string {
  if (!start) {
    return "";
  }
  if (!end) {
    return mlaDate(start);
  }
  if (sameMonth(start, end)) {
    return `${start.day}-${end.day} ${mlaMonth(start.month ?? 1)} ${start.year}`;
  }
  return mlaDateRange(start, end);
}

/** Chicago's meeting date: "April 26–28, 2016"; across months "May 30–June 2, 2019". */
export function chicagoMeetingDates(
  start: PartialDate | null,
  end: PartialDate | null
): string {
  if (!start) {
    return "";
  }
  if (!end) {
    return longDate(start);
  }
  if (sameMonth(start, end)) {
    return `${MONTH_NAMES[(start.month ?? 1) - 1]} ${start.day}–${end.day}, ${start.year}`;
  }
  return chicagoDateRange(start, end);
}

/* ----------------------------------------------------------------- title case */

/**
 * The shared title case — hyphenated compounds included — lives with the legal-sources
 * helpers; this keeps the book, media and source generators that import it from here
 * on the same function.
 */
export function titleCase(text: string): string {
  return sharedTitleCase(text);
}
